import { useState } from 'react';
import { AutoComplete } from 'antd';
import { ColoredPill, MuteText } from '@elements/index';
import { RackSearchHook, useAutocompleteLoading, useDebounce } from '@hooks/index';
import type { IRackDetails } from 'types/api/location';
import { AutocompleteSearchInput } from '@components/index';

interface IAutoCompleteRackSearchProps {
  warehouseId: string;
  onResultClick?: (location: IRackDetails) => void;
  setTableSearch?: (search: string) => void;
}

const resultRenderer = (item: IRackDetails) => {
  return (
    <div className="d-flex justify-content-between p-2">
      <div className="d-flex align-items-center">
        <MuteText>Rack</MuteText>
        <span className="ml-1">{item.code}</span>
      </div>
      <div className="d-flex align-items-center">
        <MuteText className="mr-1">Zone</MuteText>
        <ColoredPill color={item.zoneId?.color}>{item.zoneId?.code}</ColoredPill>
      </div>
    </div>
  );
};

export function AutoCompleteRackSearch({
  warehouseId,
  onResultClick,
  setTableSearch,
}: IAutoCompleteRackSearchProps) {
  const [search, setSearch] = useState('');

  const finalSearch = useDebounce(search, 400);

  const { data, loading } = RackSearchHook(warehouseId, finalSearch);
  const isSearching = useAutocompleteLoading(search, finalSearch, loading);

  const options = (data ?? []).map(item => ({
    value: item._id,
    label: resultRenderer(item),
  }));

  return (
    <AutoComplete
      value={search}
      options={options}
      filterOption={false}
      placeholder="Seach Rack"
      classNames={{ popup: { root: 'auto-search-dropdown' } }}
      onSearch={value => {
        setSearch(value);
        setTableSearch?.(value);
      }}
      onSelect={value => {
        const item = data.find(d => d._id === value);
        if (item) {
          onResultClick?.(item);
          setSearch('');
        }
      }}
    >
      <AutocompleteSearchInput
        isSearching={isSearching}
        onClear={() => {
          setSearch('');
          setTableSearch?.('');
        }}
      />
    </AutoComplete>
  );
}
